import React from 'react';
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

export interface ListItem {
  id: string;
  title: string;
  /** One short line under the title, e.g. "63 rows · 6 days ago". */
  meta?: string;
  /** A small glyph for what kind of thing it is — a table, a chart. */
  icon?: React.ReactNode;
  /** Arrived since it was last opened: a dot, said as "new" to screen readers. */
  unread?: boolean;
}

export interface ItemListProps {
  /** Named above the list, with a count — "In this session". */
  heading: string;
  items: ListItem[];
  /** The one open now; highlighted. */
  activeId?: string | null;
  onSelect: (id: string) => void;
  /** The first items are loading. */
  loading?: boolean;
  /** Said when there is nothing to list, e.g. what would put something here. */
  emptyMessage?: string;
}

/**
 * A titled list of things to open, in a sidebar or a panel — the results a
 * chat has produced, recent reports. Fills the height it's given and
 * scrolls; the heading stays put.
 *
 * - Names wrap to two lines rather than being cut off.
 * - The open item takes the sidebar's highlight, the same as the main nav.
 * - A new item at the top is scrolled into view.
 *
 * For conversations grouped by day, use `ConversationList`; for choosing one
 * option in a form, `TilePicker`.
 */
export function ItemList({
  heading,
  items,
  activeId,
  onSelect,
  loading = false,
  emptyMessage = 'Nothing here yet.',
}: ItemListProps) {
  const headingId = React.useId();
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const firstId = items[0]?.id;
  const lastFirstId = React.useRef(firstId);

  React.useEffect(() => {
    if (firstId && lastFirstId.current && firstId !== lastFirstId.current) {
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      scrollRef.current?.scrollTo?.({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    }
    lastFirstId.current = firstId;
  }, [firstId]);

  return (
    <Box
      component="section"
      aria-labelledby={headingId}
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.5, pb: 0.75, flexShrink: 0 }}>
        <Typography id={headingId} component="h2" sx={(t) => ({ ...t.typography.eyebrow, m: 0, color: t.tokens.text.tertiary })}>
          {heading}
        </Typography>
        {items.length > 0 && (
          <Typography
            variant="caption"
            sx={(t) => ({ ml: 'auto', color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}
          >
            {items.length}
          </Typography>
        )}
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pb: 1 }}>
        {loading && items.length === 0 ? (
          <Box aria-label={`Loading ${heading.toLowerCase()}`} sx={{ px: 1, pt: 0.5 }}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ py: 0.75 }}>
                <Skeleton variant="text" width={['75%', '60%', '85%'][i]} />
                <Skeleton variant="text" width="45%" />
              </Box>
            ))}
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="caption" component="p" sx={(t) => ({ m: 0, px: 1, py: 2, color: t.tokens.text.tertiary })}>
            {emptyMessage}
          </Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {items.map((item) => {
              const active = item.id === activeId;
              return (
                <li key={item.id}>
                  <ButtonBase
                    onClick={() => onSelect(item.id)}
                    aria-current={active ? 'true' : undefined}
                    sx={(t) => ({
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      textAlign: 'left',
                      borderRadius: `${t.ds.radius.sm}px`,
                      color: active ? t.tokens.sidebar.itemActiveFg : t.tokens.sidebar.itemFg,
                      bgcolor: active ? t.tokens.sidebar.itemActiveBg : 'transparent',
                      '&:hover': { bgcolor: active ? t.tokens.sidebar.itemActiveBg : t.tokens.sidebar.itemHoverBg },
                      '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
                    })}
                  >
                    {item.icon && (
                      <Box
                        aria-hidden
                        sx={(t) => ({
                          display: 'flex',
                          flexShrink: 0,
                          // Level with the first line of the name.
                          height: `calc(${t.typography.body2.fontSize} * 1.5)`,
                          alignItems: 'center',
                          color: active ? 'inherit' : t.tokens.text.tertiary,
                        })}
                      >
                        {item.icon}
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          overflowWrap: 'anywhere',
                          lineHeight: 1.5,
                          color: 'inherit',
                          fontWeight: active ? 600 : 500,
                        }}
                      >
                        {item.title}
                      </Typography>
                      {item.meta && (
                        <Typography
                          variant="caption"
                          component="span"
                          noWrap
                          sx={(t) => ({ display: 'block', color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}
                        >
                          {item.meta}
                        </Typography>
                      )}
                    </Box>
                    {item.unread && !active && (
                      <>
                        <Box
                          component="span"
                          aria-hidden
                          sx={(t) => ({
                            width: t.spacing(1),
                            height: t.spacing(1),
                            mt: 0.75,
                            flexShrink: 0,
                            borderRadius: `${t.ds.radius.full}px`,
                            bgcolor: t.tokens.brand.primary,
                          })}
                        />
                        <Box component="span" style={visuallyHidden}>
                          (new)
                        </Box>
                      </>
                    )}
                  </ButtonBase>
                </li>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
